import "./styles/global.css";
import { useEffect, useState } from "react";
import { useAppState } from "./hooks/useAppState.js";
import { trackPageView, clarityTag, clarityIdentify } from "./lib/analytics.js";
import { Nav } from "./organisms/Nav/index.jsx";
import { AdminLayout } from "./components/AdminLayout.jsx";
import { PoLayout } from "./components/PoLayout.jsx";
import { EmailOnboarding } from "./pages/EmailOnboarding.jsx";
import { MagicLinkVerify } from "./pages/MagicLinkVerify.jsx";
import { AdminLogin } from "./pages/AdminLogin.jsx";
import { ProfilePage } from "./pages/ProfilePage.jsx";
import { PersonaList } from "./pages/PersonaList.jsx";
import { CreatePersona } from "./pages/CreatePersona.jsx";
import { TriviaModal } from "./pages/TriviaModal.jsx";
import { RewardShop } from "./pages/RewardShop.jsx";
import { RewardAdmin } from "./pages/RewardAdmin.jsx";
import { ApplicationsList } from "./pages/ApplicationsList.jsx";
import { ApplicationDetail } from "./pages/ApplicationDetail.jsx";
import { InviteModal } from "./pages/InviteModal.jsx";
import { Chat } from "./pages/Chat.jsx";
import { StressTest } from "./pages/StressTest.jsx";
import { Analytics } from "./pages/Analytics.jsx";
import { Directory } from "./pages/Directory.jsx";
import { AgencyDirectory } from "./pages/AgencyDirectory.jsx";
import { AdminPersonaDetail } from "./pages/AdminPersonaDetail.jsx";
import { VoterProfile } from "./pages/VoterProfile.jsx";
import { PersonaOwnerLogin } from "./pages/PersonaOwnerLogin.jsx";
import { PersonaOwnerSignup } from "./pages/PersonaOwnerSignup.jsx";
import { PersonaOwnerDashboard } from "./pages/PersonaOwnerDashboard.jsx";
import { AdminDashboard } from "./pages/AdminDashboard.jsx";
import { AdminReportingDashboard } from "./pages/AdminReportingDashboard.jsx";
import { ClaimDetails } from "./pages/persona-owner/ClaimDetails.jsx";
import { Verification } from "./pages/persona-owner/Verification.jsx";
import { ApplicationStatus } from "./pages/persona-owner/ApplicationStatus.jsx";
import { StorageConsentBanner } from "./components/StorageConsentBanner.jsx";

// Views that hide ALL navigation (full-screen takeover)
const HIDE_NAV = new Set(["onboard", "auth-callback", "admin-login", "po-login", "po-signup", "claim-details", "claim-verify", "claim-status"]);

// Admin views that use the sidebar layout instead of top nav
const ADMIN_VIEWS = new Set([
  "dashboard", "reporting", "list", "create", "rewards", "applications", "application-detail",
  // Persona sub-pages (each is its own view, not a tab)
  "profile", "statement", "tone", "kb", "sessions", "chat", "cache", "credits",
  "analytics", "questions",
]);

// PO views that use the PO sidebar layout
const PO_VIEWS = new Set([
  "po-dashboard",
  // PO can access persona sub-pages (scoped to their single persona)
  "profile", "statement", "tone", "kb", "chat", "cache", "credits",
  "analytics", "questions",
]);

// Map view IDs to AdminPersonaDetail tab names (for views rendered by AdminPersonaDetail)
const VIEW_TO_TAB = {
  profile: "Edit Profile",
  statement: "Personal Statement",
  tone: "Tone",
  kb: "Knowledge Base",
  sessions: "Sessions",
  cache: "Cache",
  credits: "Credits",
};

// Shared style for every full-screen view wrapper
const VIEW = { flex: 1, minHeight: 0 };

export default function App() {
  const s = useAppState();
  const [previewMode, setPreviewMode] = useState("chat"); // "chat" | "stress"

  // ── Analytics: track SPA route changes ─────────────────────────────────────
  useEffect(() => {
    trackPageView(`/${s.view}`, `QuestionMe — ${s.view}`);
  }, [s.view]);

  // Tag Clarity session with voter country — use opaque hash, never raw phone (PII)
  useEffect(() => {
    if (s.voter?.country) {
      clarityTag("voter_country", s.voter.country);
      const raw = s.voter.phone || "anon";
      const hash = Array.from(raw).reduce((h, c) => ((h << 5) - h + c.charCodeAt(0)) | 0, 0);
      clarityIdentify(`voter_${Math.abs(hash).toString(36)}`);
    }
  }, [s.voter?.country, s.voter?.phone]);

  // Determine if current view is an admin view with sidebar
  const isAdminView = s.admin && s.adminAuthed && ADMIN_VIEWS.has(s.view);

  // Determine if current view is a PO view with limited sidebar
  const isPoView = s.po && s.poAuthed && !s.admin && PO_VIEWS.has(s.view);

  // For persona sub-pages rendered by AdminPersonaDetail, force the correct tab
  const forcedTab = VIEW_TO_TAB[s.view] || null;

  // ── Shared persona sub-page content (used by both admin and PO) ─────
  const personaSubPages = (isAdminView || isPoView) ? (
    <>
      {/* Persona sub-pages — each rendered by AdminPersonaDetail with a forced tab */}
      {forcedTab && s.cur && (
        <div className="qm-scrollable" style={VIEW}>
          <AdminPersonaDetail
            persona={s.cur}
            activeTab={forcedTab}
            onTabChange={(tab) => {
              if (tab === "Preview Chat") {
                window.history.pushState({}, "", `/admin/personas/${s.cur.id}/chat`);
                s.setView("chat");
                return;
              }
              const entry = Object.entries(VIEW_TO_TAB).find(([, v]) => v === tab);
              if (entry) {
                window.history.pushState({}, "", `/admin/personas/${s.cur.id}/${entry[0] === "profile" ? "" : entry[0]}`);
                s.setView(entry[0]);
              }
            }}
            onBack={() => {
              if (isPoView) {
                window.history.pushState({}, "", "/owner/dashboard");
                s.setView("po-dashboard");
              } else {
                window.history.pushState({}, "", "/");
                s.setView("list");
                s.setSel(null);
              }
            }}
            onPersonaUpdate={p => s.updatePersona(p)}
          />
        </div>
      )}

      {/* Analytics — its own page */}
      {s.view === "analytics" && s.cur && (
        <div className="qm-scrollable" style={VIEW}>
          <Analytics persona={s.cur} onBack={() => {
            if (isPoView) {
              window.history.pushState({}, "", "/owner/dashboard");
              s.setView("po-dashboard");
            } else {
              window.history.pushState({}, "", `/admin/personas/${s.cur.id}`);
              s.setView("profile");
            }
          }} />
        </div>
      )}

      {/* Questions — extracted cluster section from Analytics */}
      {s.view === "questions" && s.cur && (
        <div className="qm-scrollable" style={VIEW}>
          <Analytics persona={s.cur} showOnly="clusters" onBack={() => { window.history.pushState({}, "", `/admin/personas/${s.cur.id}/analytics`); s.setView("analytics"); }} />
        </div>
      )}

      {/* Preview Chat — its own page (with stress test toggle for admins) */}
      {s.view === "chat" && s.cur && (
        <div style={{...VIEW, display:"flex", flexDirection:"column", overflow:"hidden"}}>
          {/* Mode toggle — admin only */}
          {s.admin && (
            <div style={{ display: "flex", gap: 0, margin: "0 0 0", borderRadius: 0, overflow: "hidden", borderBottom: "1px solid #1e2d45", flexShrink: 0 }}>
              <button
                onClick={() => setPreviewMode("chat")}
                style={{
                  flex: 1, padding: "10px 0", fontSize: 13, fontWeight: 600, cursor: "pointer",
                  border: "none", transition: "all 0.2s",
                  background: previewMode === "chat" ? "#2563eb" : "#0f1623",
                  color: previewMode === "chat" ? "#fff" : "#64748b",
                }}
              >
                Manual Chat
              </button>
              <button
                onClick={() => setPreviewMode("stress")}
                style={{
                  flex: 1, padding: "10px 0", fontSize: 13, fontWeight: 600, cursor: "pointer",
                  border: "none", borderLeft: "1px solid #1e2d45", transition: "all 0.2s",
                  background: previewMode === "stress" ? "#dc2626" : "#0f1623",
                  color: previewMode === "stress" ? "#fff" : "#64748b",
                }}
              >
                Stress Test
              </button>
            </div>
          )}

          {previewMode === "chat" || !s.admin ? (
            <Chat
              persona={s.cur}
              onBack={() => {
                if (isPoView) {
                  window.history.pushState({}, "", "/owner/dashboard");
                  s.setView("po-dashboard");
                } else {
                  window.history.pushState({}, "", `/admin/personas/${s.cur.id}`);
                  s.setView("profile");
                }
              }}
              onFeedback={s.addFb}
              onUpdateConv={s.updateConv}
              xpState={s.xpState}
              onXpEarned={s.handleXpEarned}
              onPlayTrivia={s.handlePlayTrivia}
              voterPhone={s.voter?.phone}
              voterEmail={s.voter?.email}
              isAdmin={s.admin}
            />
          ) : (
            <div style={{ flex: 1, overflow: "auto", padding: 16 }}>
              <StressTest persona={s.cur} />
            </div>
          )}
        </div>
      )}
    </>
  ) : null;

  // ── Admin-only content (rendered inside AdminLayout) ────────────────
  const adminContent = isAdminView ? (
    <>
      {s.view === "dashboard" && (
        <div className="qm-scrollable" style={VIEW}>
          <AdminDashboard
            personas={s.personas}
            onSelectPersona={p => { s.setSel(p); window.history.pushState({}, "", `/admin/personas/${p.id}/analytics`); s.setView("analytics"); }}
            onSetView={s.setView}
          />
        </div>
      )}
      {s.view === "reporting" && (
        <div className="qm-scrollable" style={VIEW}>
          <AdminReportingDashboard />
        </div>
      )}
      {s.view === "list" && (
        <div className="qm-scrollable" style={VIEW}>
          <PersonaList
            personas={s.personas}
            personaTotal={s.personaTotal}
            personaPage={s.personaPage}
            pageSize={s.PERSONA_PAGE_SIZE}
            onPageChange={s.setPersonaPage}
            onSelect={p => { s.setSel(p); window.history.pushState({}, "", `/admin/personas/${p.id}/chat`); s.setView("chat"); }}
            onNew={() => {
              // v1.28.0 (AGCY): pass the current tab through to CreatePersona
              // via sessionStorage so the new persona defaults to the right
              // type (the "+ New" button from the Agencies tab creates an
              // agency, from the Officials tab creates an official).
              sessionStorage.setItem("qm_create_persona_type", s.adminPersonaTab);
              window.history.pushState({}, "", "/admin/create");
              s.setView("create");
            }}
            onAnalytics={p => { s.setSel(p); window.history.pushState({}, "", `/admin/personas/${p.id}/analytics`); s.setView("analytics"); }}
            onProfile={p => { s.setSel(p); window.history.pushState({}, "", `/admin/personas/${p.id}`); s.setView("profile"); }}
            // v1.28.0 (AGCY) — tab state lifted to useAppState so switching
            // tabs triggers a re-fetch with the right ?type= filter.
            personaType={s.adminPersonaTab}
            onPersonaTypeChange={s.setAdminPersonaTab}
          />
        </div>
      )}
      {s.view === "create" && (
        <div className="qm-scrollable" style={VIEW}>
          <CreatePersona
            onCreated={s.addPersona}
            onBack={() => {
              // v1.28.0 (AGCY): back always returns to the Personas list.
              // If the user was creating an agency we flip the list tab to
              // Agencies so the newly-created row (or the empty state) is
              // what they land on.
              const returnType = sessionStorage.getItem("qm_create_persona_type") || "official";
              sessionStorage.removeItem("qm_create_persona_type");
              if (returnType === "agency") s.setAdminPersonaTab("agency");
              else s.setAdminPersonaTab("official");
              window.history.pushState({}, "", "/admin/personas");
              s.setView("list");
            }}
          />
        </div>
      )}
      {s.view === "rewards" && (
        <div className="qm-scrollable" style={VIEW}>
          <RewardAdmin onBack={() => { window.history.pushState({}, "", "/"); s.setView("list"); }} />
        </div>
      )}
      {s.view === "applications" && (
        <div className="qm-scrollable" style={VIEW}>
          <ApplicationsList onSelect={(app) => { s.setSelectedApplication(app); window.history.pushState({}, "", `/admin/applications/${app.id}`); s.setView("application-detail"); }} />
        </div>
      )}
      {s.view === "application-detail" && s.selectedApplication && (
        <div className="qm-scrollable" style={VIEW}>
          <ApplicationDetail
            applicationId={s.selectedApplication.id}
            onBack={() => { window.history.pushState({}, "", "/admin/applications"); s.setView("applications"); }}
            onStatusChange={() => { /* refresh will happen via re-render */ }}
          />
        </div>
      )}

      {personaSubPages}
    </>
  ) : null;

  // ── PO content (rendered inside PoLayout) ───────────────────────────
  const poContent = isPoView ? (
    <>
      {s.view === "po-dashboard" && (
        <div className="qm-scrollable" style={VIEW}>
          <PersonaOwnerDashboard
            persona={s.poPersona}
            onNavigate={(viewId) => {
              if (s.poPersona) {
                const pid = s.poPersona.id;
                const urlMap = { kb: "kb", chat: "chat", analytics: "analytics", profile: "" };
                const urlSuffix = urlMap[viewId] ?? viewId;
                window.history.pushState({}, "", `/admin/personas/${pid}/${urlSuffix}`);
              }
              s.setView(viewId);
            }}
          />
        </div>
      )}
      {personaSubPages}
    </>
  ) : null;

  return (
    <div className="qm-app-root" style={{background:"#090e1a",color:"#e2e8f0",fontFamily:"system-ui,-apple-system,sans-serif"}}>

      {/* Offline banner */}
      {s.apiOffline && (
        <div style={{background:"#7c2d12",color:"#fcd34d",fontSize:12,textAlign:"center",padding:"6px 16px",flexShrink:0,zIndex:999,cursor:"pointer"}}
          onClick={() => window.location.reload()}>
          ⚠ Offline — messages won't be saved. Tap to retry.
        </div>
      )}

      {/* ── Admin views: sidebar layout ─────────────────────────────────── */}
      {isAdminView && (
        <AdminLayout
          view={s.view}
          personas={s.personas}
          selectedPersona={s.cur}
          onSetView={s.setView}
          onSelectPersona={p => { s.setSel(p); window.history.pushState({}, "", `/admin/personas/${p.id}/analytics`); s.setView("analytics"); }}
          onLogout={s.handleAdminLogout}
        >
          {adminContent}
        </AdminLayout>
      )}

      {/* ── PO views: limited sidebar layout ──────────────────────────── */}
      {isPoView && (
        <PoLayout
          view={s.view}
          persona={s.poPersona}
          onSetView={s.setView}
          onLogout={s.handlePoLogout}
        >
          {poContent}
        </PoLayout>
      )}

      {/* ── Citizen views: top nav layout ───────────────────────────────── */}
      {!isAdminView && !isPoView && (
        <>
          {!HIDE_NAV.has(s.view) && (
            <Nav
              view={s.view}
              admin={s.admin}
              voter={s.voter}
              voterXp={s.voterXp}
              rewards={s.rewards}
              onHome={() => { window.history.pushState({}, "", "/"); s.setView(s.admin ? "list" : "dir"); s.setSel(null); }}
              onSetView={s.setView}
              onAdminLogout={s.handleAdminLogout}
              onVoterUpdate={s.handleVoterUpdate}
            />
          )}

          {s.view === "onboard" && <div className="qm-scrollable" style={VIEW}><EmailOnboarding onComplete={s.handleOnboardComplete}/></div>}
          {s.view === "auth-callback" && <div className="qm-scrollable" style={VIEW}><MagicLinkVerify onVerified={(email, voter) => { s.handleMagicLinkVerified(email, voter); }} onAdminVerified={(role, me) => { if (role === "admin") { s.handleAdminLoginSuccess(); } else if (role === "persona_owner") { s.handlePoLoginSuccess({ personaId: me.personaId, email: me.email }); } }} onError={() => { window.history.pushState({}, "", "/"); s.setView("onboard"); }}/></div>}
          {s.view === "admin-login" && <div className="qm-scrollable" style={VIEW}><AdminLogin onSuccess={s.handleAdminLoginSuccess} onPoLogin={s.handlePoLoginSuccess} onSignup={() => { window.history.pushState({}, "", "/admin/signup"); s.setView("po-signup"); }} onCancel={() => { window.history.pushState({}, "", "/"); s.setView(s.voter ? "dir" : "onboard"); }}/></div>}
          {s.view === "dir" && <div className="qm-scrollable" style={VIEW}><Directory personas={s.personas} voter={s.voter} onSelect={p => { s.setSel(p); window.history.pushState({}, "", `/${p.slug}/chat`); s.setView("chat"); }} onProfile={p => { s.setSel(p); window.history.pushState({}, "", `/${p.slug}`); s.setView("profile"); }}/></div>}
          {/* v1.28.0 (AGCY): voter-facing Agencies directory — reuses the
              Directory grid layout, same slug-based deep-link pattern. */}
          {s.view === "agency-dir" && <div className="qm-scrollable" style={VIEW}><AgencyDirectory personas={s.personas} voter={s.voter} onSelect={p => { s.setSel(p); window.history.pushState({}, "", `/${p.slug}/chat`); s.setView("chat"); }} onProfile={p => { s.setSel(p); window.history.pushState({}, "", `/${p.slug}`); s.setView("profile"); }}/></div>}
          {/* v1.28.0 (AGCY): profile Back button routes to /agencies when
              the opened persona is an agency, otherwise to the Officials
              directory. Type is read off the persona row (persona_type
              field) which the backend always returns. */}
          {s.view === "profile" && s.cur && !s.admin && !s.po && <div className="qm-scrollable" style={VIEW}><ProfilePage persona={s.cur} onStartChat={() => { window.history.pushState({}, "", `/${s.cur.slug}/chat`); s.setView("chat"); }} onBack={() => {
            const isAgency = s.cur?.persona_type === "agency" || s.cur?.personaType === "agency";
            const backUrl = isAgency ? "/agencies" : "/";
            const backView = isAgency ? "agency-dir" : "dir";
            window.history.pushState({}, "", backUrl);
            s.setView(backView);
            s.setSel(null);
          }}/></div>}
          {s.view === "chat" && s.cur && !s.admin && !s.po && <div style={{...VIEW, display:"flex", flexDirection:"column", overflow:"hidden"}}><Chat persona={s.cur} onBack={() => { s.navBack(); }} onFeedback={s.addFb} onUpdateConv={s.updateConv} xpState={s.xpState} onXpEarned={s.handleXpEarned} onPlayTrivia={s.handlePlayTrivia} voterPhone={s.voter?.phone} voterEmail={s.voter?.email} isAdmin={false}/></div>}
          {s.view === "voter-profile" && <div className="qm-scrollable" style={VIEW}><VoterProfile voter={s.voter} voterXp={s.voterXp} onBack={() => { window.history.pushState({}, "", "/"); s.setView("dir"); }} onVoterUpdate={s.handleVoterUpdate} onLogout={s.handleResetVoter} onSetView={s.setView}/></div>}
          {s.view === "shop" && <div className="qm-scrollable" style={VIEW}><RewardShop xp={s.voterXp} items={s.rewards} voter={s.voter} onSpendXp={s.handleSpendXp} onBack={() => { window.history.pushState({}, "", "/"); s.setView(s.admin ? "list" : "dir"); }}/></div>}
          {s.view === "po-login" && <div className="qm-scrollable" style={VIEW}><PersonaOwnerLogin onSuccess={() => { window.history.pushState({}, "", "/"); s.setView(s.admin ? "list" : "dir"); }} onSignup={() => { window.history.pushState({}, "", "/manage/signup"); s.setView("po-signup"); }} onCancel={() => { window.history.pushState({}, "", "/"); s.setView(s.voter ? "dir" : "onboard"); }}/></div>}
          {s.view === "po-signup" && <div className="qm-scrollable" style={VIEW}><PersonaOwnerSignup onSuccess={() => { window.history.pushState({}, "", "/owner/claim"); s.setView("claim-details"); }} onLogin={() => { window.history.pushState({}, "", "/manage"); s.setView("admin-login"); }} onCancel={() => { window.history.pushState({}, "", "/"); s.setView(s.voter ? "dir" : "onboard"); }}/></div>}

          {/* ── Persona Owner Claim Flow ── */}
          {s.view === "claim-details" && <div className="qm-scrollable" style={VIEW}><ClaimDetails application={s.poApplication} onUpdate={app => s.setPoApplication(app)} onNext={() => { window.history.pushState({}, "", "/owner/verify"); s.setView("claim-verify"); }}/></div>}
          {s.view === "claim-verify" && <div className="qm-scrollable" style={VIEW}><Verification application={s.poApplication} onUpdate={app => s.setPoApplication(app)} onNext={() => { window.history.pushState({}, "", "/owner/status"); s.setView("claim-status"); }} onBack={() => { window.history.pushState({}, "", "/owner/claim"); s.setView("claim-details"); }}/></div>}
          {s.view === "claim-status" && <div className="qm-scrollable" style={VIEW}><ApplicationStatus application={s.poApplication} onResume={() => { window.history.pushState({}, "", "/owner/claim"); s.setView("claim-details"); }} onUpload={() => { window.history.pushState({}, "", "/owner/verify"); s.setView("claim-verify"); }} onDashboard={() => { window.history.pushState({}, "", "/owner/dashboard"); s.setView("po-dashboard"); }} onReapply={() => { s.setPoApplication(null); window.history.pushState({}, "", "/owner/claim"); s.setView("claim-details"); }}/></div>}

          {s.view === "not-found" && (
            <div style={{...VIEW, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:16, padding:"40px 18px"}}>
              <div style={{fontSize:48}}>🔍</div>
              <h2 style={{color:"#f1f5f9", fontFamily:"Georgia,serif", margin:0}}>Profile not found</h2>
              <p style={{color:"#64748b", fontSize:14, textAlign:"center", maxWidth:300}}>This candidate profile doesn't exist or may have been removed.</p>
              {s.voter ? (
                <button onClick={() => { s.setSel(null); window.history.pushState({}, "", "/"); s.setView("dir"); }} style={{padding:"12px 24px", background:"#1e3a5f", border:"1px solid #3b82f640", borderRadius:10, color:"#60a5fa", fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:"inherit"}}>
                  ← Browse All Profiles
                </button>
              ) : (
                <div style={{display:"flex", flexDirection:"column", alignItems:"center", gap:10}}>
                  <button onClick={() => { s.setSel(null); window.history.pushState({}, "", "/"); s.setView("onboard"); }} style={{padding:"12px 24px", background:"linear-gradient(135deg,#3b82f6,#3b82f6cc)", border:"none", borderRadius:10, color:"#fff", fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:"inherit"}}>
                    Sign in to browse candidates
                  </button>
                  <span style={{color:"#475569", fontSize:12}}>Create an account to discover candidates in your area</span>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {s.triviaPersona && <TriviaModal persona={s.triviaPersona} voterPhone={s.voter?.phone || s.voter?.email} onClose={() => s.setTriviaPersona(null)} onXpEarned={s.handleTriviaXpEarned}/>}
      {/* v1.28.5 — trivia /complete failure toast. Top-of-screen non-
          blocking banner so the user sees that their XP claim didn't
          land instead of silently losing it. Auto-hides when the user
          taps dismiss; lives at the App level so it survives navigating
          away from the chat view. */}
      {s.triviaClaimError && (
        <div
          role="alert"
          data-testid="trivia-claim-error"
          style={{
            position:"fixed",top:12,left:"50%",transform:"translateX(-50%)",zIndex:1100,
            maxWidth:"min(420px, calc(100vw - 24px))",padding:"10px 14px",
            background:"#ef444418",border:"1px solid #ef444450",borderRadius:10,
            display:"flex",alignItems:"center",gap:10,color:"#fca5a5",fontSize:13,lineHeight:1.5,
            boxShadow:"0 4px 24px #00000088",
          }}
        >
          <span style={{fontSize:16}}>⚠️</span>
          <span style={{flex:1}}>{s.triviaClaimError}</span>
          <button
            onClick={() => s.setTriviaClaimError(null)}
            aria-label="Dismiss"
            style={{background:"none",border:"none",color:"#fca5a5",cursor:"pointer",fontSize:18,padding:"0 4px",lineHeight:1}}
          >×</button>
        </div>
      )}
      <StorageConsentBanner />
    </div>
  );
}
